import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import type {MetaType} from '../../../../components/Datatable/Datatable'
import {Button} from '../../../../components/Button/Button'
import useModals from '../../../../hooks/useModals'
import {DELETE, POST} from '../../../../services/api'
import {Loading} from '../../../../components/Loading/Loading'
import {ModalDelete} from '../../../../components/Modals/General/ModalDelete/ModalDelete'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../../components/Datatable/SwitchStatus'

type SatisfactionFeedbackResponseType = {
  id: number
  title: string
  description: string
  status: boolean
  created: string
  dateStart?: string
  dateEnd?: string
  content: {
    config: Array<{
      order: number
      type: string
      settings: {
        gridHotelFeedbackIds?: number[]
        customTitle?: string
      }
    }>
  }
}

const Content: FC = () => {
  const navigate = useNavigate()
  const {showDeleteModal} = useModals()
  const [loading, setLoading] = useState(false)
  const {switchStatus} = useSwitchStatus()
  const [satisfactionFeedbacks, setSatisfactionFeedbacks] = useState<
    SatisfactionFeedbackResponseType[]
  >([])
  const [id, setId] = useState<number | null>(null)
  const [, setMeta] = useState<MetaType | undefined>(undefined)

  // Função para formatar data
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    } catch {
      return '-'
    }
  }

  const getSatisfactionFeedbacks = async () => {
    try {
      setLoading(true)
      const res = await POST(`/feedbackform/filter`, {})

      // A resposta agora vem em res.dados
      const dataArray = res.dados || res.data || []

      const processedData = dataArray.map((row: SatisfactionFeedbackResponseType) => {
        const componentsCount = row.content?.config?.length || 0
        const gridsCount =
          row.content?.config?.reduce((total: number, configItem: any) => {
            return total + (configItem.settings?.gridHotelFeedbackIds?.length || 0)
          }, 0) || 0

        return {
          id: row.id,
          title: row.title,
          description: row.description,
          status: row.status,
          created: row.created,
          dateStart: row.dateStart,
          dateEnd: row.dateEnd,
          dateStartFormatted: formatDate(row.dateStart),
          dateEndFormatted: formatDate(row.dateEnd),
          components: `${componentsCount} ${
            componentsCount !== 1
              ? i18n.t('satisfaction_feedback.components_plural')
              : i18n.t('satisfaction_feedback.component')
          }`,
          grids: `${gridsCount} ${
            gridsCount !== 1
              ? i18n.t('satisfaction_feedback.grids_plural')
              : i18n.t('satisfaction_feedback.grid')
          }`,
        }
      })
      setSatisfactionFeedbacks(processedData)
      setMeta(res.meta)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  const deleteSatisfactionFeedback = async () => {
    try {
      setLoading(true)
      const res = await DELETE(`/feedbackform/${id}`)
      if (res.success === true) {
        Swal.fire({
          icon: 'success',
          title: String(i18n.t('general.success')),
          text: String(i18n.t('satisfaction_feedback.success_deleted')),
        }).then(async () => await getSatisfactionFeedbacks())
      } else {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('general.error')),
          text: String(i18n.t('satisfaction_feedback.error_delete')),
        }).then(async () => await getSatisfactionFeedbacks())
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('general.error')),
        text: String(i18n.t('satisfaction_feedback.error_delete')),
      }).then(async () => await getSatisfactionFeedbacks())
      console.log(e)
    }
  }

  useEffect(() => {
    getSatisfactionFeedbacks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('satisfaction_feedback.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('satisfaction_feedback.subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text='Dashboard Analytics'
                  color='secondary'
                  iconColor='#FFF'
                  icon='fas fa-chart-line'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/feedback-satisfaction/analytics')}
                  btnClass='mb-5 fw-bolder me-2'
                />
                <Button
                  text={i18n.t('satisfaction_feedback.create_form')}
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/feedback-satisfaction/feedback-list/create')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={satisfactionFeedbacks}
                headers={[
                  {
                    key: String(i18n.t('satisfaction_feedback.form_title')),
                    value: 'title',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('satisfaction_feedback.form_components')),
                    value: 'components',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('satisfaction_feedback.form_grids')),
                    value: 'grids',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('satisfaction_feedback.form_date_init')),
                    value: 'dateStartFormatted',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('satisfaction_feedback.form_date_end')),
                    value: 'dateEndFormatted',
                    type: 'default',
                  },
                  {
                    key: String(i18n.t('satisfaction_feedback.form_status')),
                    value: 'status',
                    type: 'check',
                    action: (item: any) =>
                      switchStatus({
                        endpoint: 'feedbackform',
                        Id: item.id,
                        status: item.status,
                      }),
                  },
                  {key: String(i18n.t('satisfaction_feedback.actions')), value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => {
                      navigate(`/hospitality/feedback-satisfaction/feedback-list/edit/${item.id}`)
                    },
                    title: String(i18n.t('satisfaction_feedback.edit_form')),
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => {
                      showDeleteModal(String(i18n.t('satisfaction_feedback.delete')))
                      setId(item.id)
                    },
                    title: String(i18n.t('satisfaction_feedback.delete_form')),
                  },
                ]}
                color='primary'
              />
            </div>
          </div>

          <ModalDelete confirmAction={() => deleteSatisfactionFeedback()} />
        </div>
      )}
    </>
  )
}

const SatisfactionFeedbackIndex: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: String(i18n.t('satisfaction_feedback.breadcrumb_dashboard')),
            path: 'dashboard',
            isActive: false,
          },
          {
            title: i18n.t('satisfaction_feedback.title') as string,
            path: 'satisfaction-feedback',
            isActive: true,
          },
        ]}
      >
        {i18n.t('satisfaction_feedback.title')}
      </PageTitle>
      <Content />
    </>
  )
}

export {SatisfactionFeedbackIndex}
